import React, { useEffect, useState } from 'react';
import { usePortal } from '../../contexts/PortalContext';
import type { Deliverable } from '../../types/portal';
import { fetchTranscriptComments, type TranscriptComment } from '../../utils/transcriptComments';
import { timecode } from '../../utils/transcriptCues';
import { formatEditStamp } from '../../utils/time';

/**
 * The comments clients have left on this transcript, exactly as stored — the
 * same rows the client sees, read here so Portal is never working from a
 * different record.
 *
 * The timecode leads each one where there is a timecode to lead with. This is
 * the side of the screen where it earns its keep: "can we tighten this" is a
 * note, "can we tighten this at 0:42" is an edit somebody can go and make.
 */
export function TranscriptComments({
  deliverable
}: {
  deliverable: Deliverable;
}) {
  const {
    activeProject,
    clientName
  } = usePortal();
  const [comments, setComments] = useState<TranscriptComment[]>([]);
  useEffect(() => {
    let live = true;
    void fetchTranscriptComments(activeProject.slug, deliverable.id, clientName).then(result => {
      if (!live || result.status !== 'ok') return;
      setComments(result.comments);
    });
    return () => {
      live = false;
    };
  }, [activeProject.slug, deliverable.id, clientName]);
  if (comments.length === 0) return null;
  return <div className="mt-12" data-id="element-1650" data-mp="4gp442pwhlcn">
      <h4 className="label border-b border-ruleDark pb-4 text-paper/40" data-id="element-1651" data-mp="j6wb9magjhy1">Transcript Comments</h4>
      <ul className="m-0 list-none p-0" data-id="element-1652" data-mp="e7tj32fkt67h">
        {comments.map(comment => {
        const stamp = comment.quoteSeconds == null ? '' : timecode(comment.quoteSeconds);
        return <li key={comment.id} className="border-b border-ruleDark py-5" data-id="element-1653" data-mp="s58b8ex7rs35">
              {stamp && <span className="label-sm block tabular-nums text-paper/50" data-id="element-1654" data-mp="2tyz8h52h8y32">{stamp}</span>}
              {comment.quote && <p className={`m-0 border-l-2 border-ruleDark pl-4 font-display text-base leading-snug text-paper/40 ${stamp ? 'mt-2' : ''}`} data-id="element-1655" data-mp="2p0xomhx88uqj">
                  “{comment.quote}”
                </p>}
              <p className="mt-2 font-display text-lg font-normal leading-relaxed text-paper/70" data-id="element-1656" data-mp="12unuxiykgatq">
                {comment.body}
              </p>
              <span className="label-sm mt-2 block text-paper/30" data-id="element-1657" data-mp="16uehfah284sg">
                {comment.authorName} · {formatEditStamp(comment.at)}
              </span>
            </li>;
      })}
      </ul>
    </div>;
}